import { useState, useEffect, useMemo } from 'react';
import {
  Button,
  Collapse,
  DatePicker,
  DatePickerProps,
  Empty,
  Select,
  Skeleton,
  Tag,
} from 'antd';

import { AllReviewsProps, menteeObjectiveProps } from '../types';
import dayjs from 'dayjs';
import { getAllQuarters } from '../services/reviewsServices';
import { getAllObjectives } from '../services/objectiveServices';
import { useLocation } from 'react-router-dom';

import { useAtomValue } from 'jotai';
import { userAtom } from '../store/atom';
import {
  quarterReviewStatuses,
  statusColors,
  Statuses,
  statusesLabel,
} from '../helper';
import RightTwoArrowCircle from './icons/RightTwoArrowCircle';
import { getQuarterTabLable } from '../helper/functions';

interface RideSideCollapsibleCompProps {
  isCollapsed: boolean;
  setIsCollapsed: (isCollapsed: boolean) => void;
}

function RideSideCollapsibleComp({
  isCollapsed,
  setIsCollapsed,
}: RideSideCollapsibleCompProps) {
  const [filteredQuarters, setFilteredQuarters] = useState<AllReviewsProps[]>(
    []
  );
  const [current, setCurrent] = useState<AllReviewsProps>();
  const [loading, setLoading] = useState(true);
  const [allQuarters, setAllQuarters] = useState<AllReviewsProps[]>();
  const [objectives, setObjectives] = useState<menteeObjectiveProps[]>([]);
  const { state } = useLocation();
  const from: 'hr' | 'mentor' | 'mentee' = state?.['from'];
  const { selectedMentee } = useAtomValue(userAtom);
  const [dataFetchError, setDataFetchError] = useState<boolean>(false);

  // filter quarter by date
  const filterQuarter = (
    data: AllReviewsProps[] | undefined,
    date: string | number
  ) => {
    if (!data) return;
    setFilteredQuarters([]);
    const filter = data?.filter(
      (item) => item.year.toString() === date.toString() && item.status !== 0
    );

    if (filter.length === 0) return setFilteredQuarters(filter);

    filter?.sort((a: AllReviewsProps, b: AllReviewsProps) =>
      dayjs(a.startDate).diff(dayjs(b.startDate))
    );

    setFilteredQuarters(filter);

    // Find active quarters (status === 1 means in progress)
    const activeQuarters = filter?.filter((item) => item.status === 1);

    if (activeQuarters && activeQuarters.length > 0) {
      // If multiple cycles are active, default to the most recent one
      activeQuarters.sort((a: any, b: any) =>
        dayjs(b.startDate).diff(dayjs(a.startDate))
      );
      setCurrent(activeQuarters[0]);
    } else {
      // If no active quarter, default to first quarter in filtered list
      setCurrent(filter[0]);
    }
  };

  const fetchAllQuarters = async () => {
    setLoading(true);
    const { data, error } = await getAllQuarters(
      from === 'mentee' ? undefined : from
    );
    setLoading(false);
    if (error) return setDataFetchError(true);
    setAllQuarters(data);
    // filter quarter by current year
    const currentYear = dayjs().format('YYYY').toString();
    filterQuarter(data, currentYear);
  };

  // fetch selected quarter objectives
  const fetchObjectives = async (id: string) => {
    setObjectives([]);
    setLoading(true);
    const { data, error } = await getAllObjectives(
      selectedMentee?.id || '',
      from === 'mentee' ? undefined : from,
      id
    );

    setLoading(false);
    if (error) return setDataFetchError(true);

    setObjectives(data);
  };

  const onChange: DatePickerProps['onChange'] = (_, dateString) => {
    filterQuarter(allQuarters, dateString as string);
  };

  const handleMenuClick = (selected: string) => {
    const find = filteredQuarters?.findIndex((item) => item.id === selected);
    if (find !== undefined && find !== -1) setCurrent(filteredQuarters?.[find]);
  };

  useEffect(() => {
    fetchAllQuarters();
  }, []);

  useEffect(() => {
    if (current?.id) {
      fetchObjectives(current?.id);
    }
  }, [current?.id]);

  return (
    <div
      className={`transition-all duration-300 ${isCollapsed ? 'w-0' : 'w-[400px] bg-[#F8F8F8] border border-[#E3E3E3] rounded-lg h-full'}`}
    >
      <div className="p-4">
        <div className="flex items-center justify-between mb-8 mt-3 ">
          <Button
            shape="circle"
            type="text"
            size="large"
            icon={
              !isCollapsed && <RightTwoArrowCircle height={28} width={28} />
            }
            onClick={() => setIsCollapsed(!isCollapsed)}
          />
          {!isCollapsed && (
            <>
              <h3 className="text-lg font-semibold text-[#000044]">
                Quarterly Feedback on Objectives
              </h3>
            </>
          )}
        </div>

        {!isCollapsed && (
          <>
            <div className="flex items-center justify-between mb-8">
              <DatePicker
                onChange={onChange}
                picker="year"
                className="w-28"
                placeholder="Year"
                defaultValue={dayjs(dayjs().format('YYYY'))}
                disabled={loading}
              />
              {/** Quarter dropdown */}
              <Select
                placeholder={'Select Quarter'}
                variant="borderless"
                value={current?.id}
                onChange={(value) => handleMenuClick(value)}
                options={filteredQuarters.map((item) => ({
                  value: item.id,
                  label: (
                    <b className="uppercase">{`${getQuarterTabLable(item.startDate, item.endDate)} ${item.status === 1 ? `(${statusesLabel[Statuses.InProgress]})` : ''}`}</b>
                  ),
                }))}
                style={{
                  backgroundColor: 'transparent',
                  width: 'auto',
                }}
              />
            </div>

            {dataFetchError ? (
              <div>
                <p>Error in data loading!</p>
              </div>
            ) : loading && !isCollapsed ? (
              <>
                <div className="space-y-4">
                  {[1, 2, 3].map((_, index) => (
                    <div key={index} className="border rounded-lg p-4">
                      <div className="flex justify-between items-center mb-4">
                        <Skeleton.Input
                          style={{ width: 200 }}
                          active
                          size="small"
                        />
                        <Skeleton.Button active size="small" shape="round" />
                      </div>
                      <Skeleton active paragraph={{ rows: 3 }} />
                    </div>
                  ))}
                </div>
              </>
            ) : filteredQuarters.length === 0 || objectives.length === 0 ? (
              <Empty />
            ) : (
              <Collapse
                defaultActiveKey={['1']}
                className="border-none bg-[#F8F8F8]"
                style={{ backgroundColor: '#F8F8F8' }}
                items={objectives.map((objective, index) => ({
                  key: `objective-${index}`,
                  label: (
                    <div className="flex justify-between items-start ">
                      <span className="text-md font-bold">
                        {objective.description}
                      </span>
                      <Label content={objective.status} key={index} />
                    </div>
                  ),
                  children: (
                    <div className="flex flex-col gap-4 bg-[#F8F8F8] m-[-20px] py-5 px-5 pl-14">
                      {/** Comments filer by selected quarter */}

                      <ReviewDropdown
                        key={index}
                        data={objective}
                        role={from}
                        quarter={current as AllReviewsProps}
                      />
                    </div>
                  ),
                }))}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default RideSideCollapsibleComp;

interface LabelProps {
  content: string;
}

const Label = ({ content }: LabelProps) => {
  return (
    <div className="flex flex-col">
      <div className={`'text-nowrap'}`}>
        <Tag color={statusColors[content as string]}>
          {statusesLabel[content as string]}
        </Tag>
      </div>
    </div>
  );
};

interface RightTwoArrowCircleProps {
  data: menteeObjectiveProps;
  role: 'mentor' | 'hr' | 'mentee';
  quarter: AllReviewsProps;
}

const ReviewDropdown = ({ data, role, quarter }: RightTwoArrowCircleProps) => {
  const filterObjective = useMemo(() => {
    const find = data.objectiveComments?.findIndex(
      (item) => item.quarterId === quarter.id
    );
    if (find !== undefined && find !== -1)
      return {
        index: find,
        comment: data.objectiveComments?.[find],
      };

    return undefined;
  }, [quarter.id]);

  // find statuses for a quarter objective comment
  const quarterStatus = useMemo(() => {
    const status = quarterReviewStatuses[quarter.status];
    return {
      isClosed: status === Statuses.Completed || status === Statuses.Closed,
      isMenteeSubmitted:
        filterObjective?.comment?.menteeCommentStatus === Statuses.Completed,
      isMentorSubmitted:
        filterObjective?.comment?.mentorCommentStatus === Statuses.Completed,
    };
  }, [quarter.status, filterObjective?.comment]);

  return (
    <div>
      <div className="bg-[#F8F8F8]">
        <div className="text-sm text-[#616161] mb-1">Expected Outcome </div>
        <p className="text-lg  font-medium text-black">
          {data.expectedOutcome}
        </p>
      </div>
      <div>
        {role === 'mentee' ||
        quarterStatus.isClosed ||
        (role === 'hr' &&
          !quarterStatus.isClosed &&
          quarterStatus.isMenteeSubmitted) ? (
          <>
            <div className="text-sm text-[#616161] mb-1 mt-2">
              {' '}
              Mentee Feedback{' '}
            </div>
            <p className="text-lg text-black">
              {filterObjective?.comment?.menteeComment || 'Not submitted yet'}
            </p>
          </>
        ) : null}
      </div>
      <div>
        {role === 'mentor' ||
        quarterStatus.isClosed ||
        (role === 'hr' &&
          !quarterStatus.isClosed &&
          quarterStatus.isMentorSubmitted) ? (
          <>
            <div className="text-sm text-[#616161] mb-1 mt-2">
              {' '}
              Mentor Feedback{' '}
            </div>
            <p className="text-lg  font-[550] text-black">
              {filterObjective?.comment?.mentorComment || 'Not submitted yet'}
            </p>
          </>
        ) : null}
      </div>
    </div>
  );
};
